import * as oauth2Api from '@/api/oauth2';
import store from '@/store';
import { Capacitor } from '@capacitor/core';

class AuthService {
  constructor() {
    this.refreshPromise = null;
    // 开发/测试开关：是否启用游客自动登录（首次无认证时）
    this.enableGuestAutoLogin = false;
    
    // 新增：小程序环境检测
    this._miniProgramEnv = this._detectMiniProgramEnv();
  }

  /**
   * 检测是否在小程序 WebView 环境中
   */
  _detectMiniProgramEnv() {
    // 检测是否在小程序 WebView 中
    const ua = navigator.userAgent.toLowerCase();
    const isMiniProgram = ua.includes('miniprogram') || 
                         window.__wxjs_environment === 'miniprogram';
    
    console.log('[AuthService] 检测小程序环境:', {
      isMiniProgram,
      userAgent: navigator.userAgent,
      wxEnv: window.__wxjs_environment
    });
    
    return isMiniProgram;
  }

  /**
   * 检查是否为原生App环境
   */
  isNativePlatform() {
    try {
      return typeof Capacitor !== 'undefined' && Capacitor.isNativePlatform && Capacitor.isNativePlatform();
    } catch (error) {
      return false;
    }
  }

  /**
   * 检查是否在小程序环境中
   */
  isMiniProgram() {
    return this._miniProgramEnv;
  }

  /**
   * 检查是否启用免登功能（只在原生App中启用）
   * 注意：项目是app的时候取消自动登录
   */
  isAutoLoginEnabled() {
    // 项目是app的时候取消自动登录
    if (!this.isNativePlatform()) {
      return false;
    }

    try {
      const rememberedTypeRaw = localStorage.getItem('xpzRememberedAccountType');
      if (rememberedTypeRaw && Number(JSON.parse(rememberedTypeRaw)) === 6) {
        return true;
      }
    } catch (error) {
      console.warn('[AutoLogin] Failed to parse remembered account type:', error);
    }

    try {
      const authDataStr = localStorage.getItem('xpzAuthData');
      if (authDataStr) {
        const authData = JSON.parse(authDataStr);
        if (Number(authData && authData.accountType) === 6) {
          return true;
        }
      }
    } catch (error) {
      console.warn('[AutoLogin] Failed to parse auth data account type:', error);
    }

    return false;
  }

  getRememberedPasswordLogin() {
    try {
      const mobile = localStorage.getItem('xpzRememberedMobile') || '';
      const password = localStorage.getItem('xpzRememberedPassword') || '';
      const accountTypeRaw = localStorage.getItem('xpzRememberedAccountType');
      const blocked = localStorage.getItem('xpzAutoLoginBlocked') === 'true';

      return {
        mobile,
        password,
        accountType: accountTypeRaw ? JSON.parse(accountTypeRaw) : null,
        blocked
      };
    } catch (error) {
      console.warn('[AutoLogin] Failed to read remembered password login:', error);
      return {
        mobile: '',
        password: '',
        accountType: null,
        blocked: false
      };
    }
  }

  /**
   * 检查是否已认证
   */
  isAuthenticated() {
    // 如果是小程序环境，也使用传统方式检查（因为小程序可能使用 URL 参数传递 token）
    if (this.isMiniProgram() || !this.isAutoLoginEnabled()) {
      // 小程序环境或非原生平台，使用传统的sessionStorage检查
      try {
        const userInfoStr = sessionStorage.getItem("userInfoXpz");
        if (userInfoStr) {
          const userInfo = JSON.parse(userInfoStr);
          return userInfo && userInfo.access_token;
        }
      } catch (e) {
        console.warn('[Auth] Error checking traditional auth:', e);
      }
      return false;
    }
    return store.getters.isAuthenticated && !this.isTokenExpired();
  }

  /**
   * 检查token是否过期
   */
  isTokenExpired() {
    if (this.isMiniProgram() || !this.isAutoLoginEnabled()) {
      return false; // 小程序或非原生平台不检查token过期
    }
    return store.getters.isTokenExpired;
  }

  /**
   * 获取访问令牌
   */
  getAccessToken() {
    // 如果是小程序环境，优先从sessionStorage获取
    if (this.isMiniProgram() || !this.isAutoLoginEnabled()) {
      try {
        const userInfoStr = sessionStorage.getItem("userInfoXpz");
        if (userInfoStr) {
          const userInfo = JSON.parse(userInfoStr);
          return userInfo?.access_token;
        }
      } catch (e) {
        console.warn('[Auth] Error getting token from sessionStorage:', e);
      }
      return null;
    }
    return store.getters.accessToken;
  }

  /**
   * 获取用户信息
   */
  getUserInfo() {
    if (this.isMiniProgram() || !this.isAutoLoginEnabled()) {
      try {
        const userInfoStr = sessionStorage.getItem("userInfoXpz");
        return userInfoStr ? JSON.parse(userInfoStr) : null;
      } catch (e) {
        console.warn('[Auth] Error getting userInfo from sessionStorage:', e);
        return null;
      }
    }
    return store.getters.userInfo;
  }

  /**
   * 获取账号类型
   */
  getAccountType() {
    if (this.isMiniProgram() || !this.isAutoLoginEnabled()) {
      try {
        const accountTypeStr = sessionStorage.getItem("accountTypeXpz");
        return accountTypeStr ? JSON.parse(accountTypeStr) : null;
      } catch (e) {
        console.warn('[Auth] Error getting accountType from sessionStorage:', e);
        return null;
      }
    }
    return store.getters.accountType;
  }

  /**
   * 登录成功处理
   */
  async handleLoginSuccess(loginResponse) {
    const { access_token, refresh_token, ...userInfo } = loginResponse;
    try {
      localStorage.removeItem('xpzAutoLoginBlocked');
    } catch (e) {
      console.warn('[Auth] Failed to clear auto-login block flag:', e);
    }

    // 小程序 / 原生 App 均需同步 Vuex。
    // 原生端首次登录时 isAutoLoginEnabled() 仍为 false（尚无 xpzAuthData），
    // 若不同步 store，登录后 isAutoLoginEnabled 变为 true 时路由守卫只认 Vuex，会导致需点两次登录。
    if (this.isMiniProgram() || this.isNativePlatform()) {
      if (store && store.dispatch) {
        try {
          await store.dispatch('loginSuccess', {
            userInfo,
            accessToken: access_token,
            refreshToken: refresh_token,
            accountType: userInfo.accountType
          });
        } catch (e) {
          console.warn('[Auth] 更新 Vuex store 失败:', e);
        }
      }
    }

    // 兼容性：始终将用户信息和持久化认证数据写入 localStorage/sessionStorage
    try {
      // 计算过期时间
      let expiryMs = Date.now() + (2 * 60 * 60 * 1000);
      if (loginResponse.expires_in && Number(loginResponse.expires_in) > 0) {
        expiryMs = Date.now() + Number(loginResponse.expires_in) * 1000;
      }

      const authData = {
        userInfo,
        accessToken: access_token,
        refreshToken: refresh_token,
        accountType: userInfo.accountType,
        tokenExpiry: expiryMs
      };

      // 持久化到 localStorage（用于冷启动恢复）
      localStorage.setItem('xpzAuthData', JSON.stringify(authData));

      // 兼容老逻辑：sessionStorage 保存 userInfo/accountType
      try {
        const compatUserInfo = Object.assign({}, userInfo, { access_token });
        sessionStorage.setItem('userInfoXpz', JSON.stringify(compatUserInfo));
      } catch (e) {
        sessionStorage.setItem('userInfoXpz', JSON.stringify(userInfo));
      }
      sessionStorage.setItem('accountTypeXpz', JSON.stringify(userInfo.accountType));
      
      // 如果是小程序环境，额外保存一个标记，表示是通过小程序登录的
      if (this.isMiniProgram()) {
        sessionStorage.setItem('loginFromMiniProgram', 'true');
      }
    } catch (e) {
      console.warn('[Auth] 保存认证信息到 localStorage 失败:', e);
    }

    return userInfo;
  }

  /**
   * 小程序自动登录（新增方法）
   * @param {string} token 小程序传递的token
   */
  async miniProgramAutoLogin(token) {
    if (!this.isMiniProgram()) {
      console.warn('[Auth] 不在小程序环境中，跳过小程序自动登录');
      return {
        success: false,
        message: '不在小程序环境中'
      };
    }

    try {
      console.log('[Auth] 开始小程序自动登录，token:', token.substring(0, 10) + '...');
      
      // 1. 先保存 token 到本地，以便后续使用
      localStorage.setItem('miniProgramToken', token);
      
      // 2. 调用后端接口验证 token（这里需要根据你的实际接口调整）
      const verifyResult = await this.verifyMiniProgramToken(token);
      
      if (verifyResult.success) {
        // 3. 验证成功，处理登录成功
        const loginResult = await this.handleLoginSuccess({
          ...verifyResult.data,
          access_token: verifyResult.data.token || token
        });
        
        console.log('[Auth] 小程序自动登录成功');
        return {
          success: true,
          data: loginResult
        };
      } else {
        console.error('[Auth] 小程序token验证失败:', verifyResult.message);
        return {
          success: false,
          message: verifyResult.message
        };
      }
    } catch (error) {
      console.error('[Auth] 小程序自动登录异常:', error);
      return {
        success: false,
        message: error.message
      };
    }
  }

  /**
   * 验证小程序 token（新增方法）
   * 调用后端接口验证小程序传递的 token
   */
  async verifyMiniProgramToken(token) {
    try {
      // 这里调用你的后端验证接口
      // 注意：根据你的实际接口调整
      const response = await fetch('/api/auth/verify-miniprogram-token', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      
      const result = await response.json();
      console.log('[Auth] 小程序token验证结果:', result);
      
      return result;
    } catch (error) {
      console.error('[Auth] 验证小程序token失败:', error);
      return {
        success: false,
        message: error.message
      };
    }
  }

  /**
   * 检查并处理小程序自动登录（新增方法）
   * 在登录页面调用，自动检查URL参数或等待小程序消息
   */
  async checkAndHandleMiniProgramLogin() {
    if (!this.isMiniProgram()) {
      return {
        isMiniProgram: false,
        autoLogging: false
      };
    }
    
    console.log('[Auth] 检测到小程序环境，检查自动登录...');
    
    // 1. 检查URL中是否有token参数
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('token');
    
    if (token) {
      console.log('[Auth] 从URL参数获取到token，开始自动登录');
      return {
        isMiniProgram: true,
        autoLogging: true,
        token: token
      };
    }
    
    // 2. 检查本地是否已有登录状态
    if (this.isAuthenticated()) {
      console.log('[Auth] 小程序环境中已有登录状态');
      return {
        isMiniProgram: true,
        autoLogging: false,
        alreadyLoggedIn: true
      };
    }
    
    // 3. 等待小程序通过postMessage发送token
    console.log('[Auth] 等待小程序发送登录信息...');
    return {
      isMiniProgram: true,
      autoLogging: true,
      waitingForToken: true
    };
  }

  /**
   * 处理小程序发送的消息（新增方法）
   * 在Login.vue的消息监听器中调用
   */
  handleMiniProgramMessage(data) {
    if (!this.isMiniProgram()) return null;
    
    console.log('[Auth] 处理小程序消息:', data);
    
    if (data.type === 'MP_LOGIN_RESULT' || data.type === 'MINI_PROGRAM_TOKEN') {
      if (data.success === false) {
        return {
          success: false,
          message: data.message || '小程序登录失败'
        };
      }
      
      const token = data.token;
      if (token) {
        return {
          success: true,
          token: token,
          userId: data.userId,
          action: data.action
        };
      }
    }
    
    return null;
  }

  /**
   * 登出处理
   */
  async logout() {
    try {
      localStorage.setItem('xpzAutoLoginBlocked', 'true');
    } catch (e) {
      console.warn('[Auth] Failed to persist auto-login block flag:', e);
    }
    // 清除小程序相关标记
    if (this.isMiniProgram()) {
      localStorage.removeItem('miniProgramToken');
      sessionStorage.removeItem('loginFromMiniProgram');
    }
    
    if (this.isAutoLoginEnabled()) {
      await store.dispatch('logout');
    } else {
      // 非原生平台只清除sessionStorage
      sessionStorage.removeItem('userInfoXpz');
      sessionStorage.removeItem('accountTypeXpz');
    }
    localStorage.removeItem('xpzAuthData');
  }

  // 以下原有方法保持不变...
  
  /**
   * 刷新访问令牌
   */
  async refreshAccessToken() {
    if (!this.isAutoLoginEnabled()) {
      throw new Error('免登功能仅在原生App中启用');
    }

    if (this.refreshPromise) {
      return this.refreshPromise;
    }

    const refreshToken = store.getters.refreshToken;
    if (!refreshToken) {
      throw new Error('没有刷新令牌');
    }

    store.dispatch('setRefreshingToken', true);

    this.refreshPromise = this._performTokenRefresh(refreshToken)
      .finally(() => {
        this.refreshPromise = null;
        store.dispatch('setRefreshingToken', false);
      });

    return this.refreshPromise;
  }

  /**
   * 执行token刷新
   */
  async _performTokenRefresh(refreshToken) {
    try {
      const response = await this._callRefreshTokenAPI(refreshToken);

      if (response && response.access_token) {
        await store.dispatch('refreshToken', {
          accessToken: response.access_token,
          refreshToken: response.refresh_token
        });

        return response.access_token;
      } else {
        throw new Error('刷新token失败');
      }
    } catch (error) {
      console.error('Token刷新失败:', error);
      await this.logout();
      throw error;
    }
  }

  /**
   * 调用刷新token的API
   */
  async _callRefreshTokenAPI(refreshToken) {
    const response = await oauth2Api.refreshTokenInfo({
      refreshToken: refreshToken
    });

    if (response && response.status && response.ret) {
      return {
        access_token: response.ret.access_token,
        refresh_token: response.ret.refresh_token
      };
    }

    throw new Error('刷新token响应格式错误');
  }

  /**
   * 从本地存储恢复认证状态
   */
  async restoreAuthFromStorage() {
    if (!this.isAutoLoginEnabled()) {
      console.log('[AutoLogin] restoreAuthFromStorage skipped (not native platform)');
      return false;
    }

    console.log('[AutoLogin] restoreAuthFromStorage start', {
      hasAuthData: !!localStorage.getItem('xpzAuthData'),
      rememberedAccountType: localStorage.getItem('xpzRememberedAccountType'),
      blocked: localStorage.getItem('xpzAutoLoginBlocked')
    });

    const success = await store.dispatch('loadAuthFromStorage');
    if (success) {
      const userInfo = this.getUserInfo();
      if (userInfo) {
        try {
          sessionStorage.setItem('userInfoXpz', JSON.stringify(userInfo));
          sessionStorage.setItem('accountTypeXpz', JSON.stringify(this.getAccountType()));
        } catch (e) {
          console.warn('[AutoLogin] restoreAuthFromStorage - set sessionStorage failed:', e);
        }
      }
      console.log('[AutoLogin] restoreAuthFromStorage - success, userInfo:', userInfo && userInfo.accountType);
      try {
        const { onUserLoggedIn } = await import('@/utils/push');
        await onUserLoggedIn();
        console.log('[AutoLogin] restoreAuthFromStorage - onUserLoggedIn invoked');
      } catch (pushErr) {
        console.warn('[AutoLogin] restoreAuthFromStorage - onUserLoggedIn failed:', pushErr);
      }
    } else {
      console.log('[AutoLogin] restoreAuthFromStorage - no valid auth data found');
    }
    return success;
  }

  /**
   * 尝试自动登录
   */
  async tryAutoLogin() {
    console.log('[AutoLogin] tryAutoLogin invoked, isAutoLoginEnabled=', this.isAutoLoginEnabled());
    if (!this.isAutoLoginEnabled()) {
      console.log('[AutoLogin] tryAutoLogin aborted (not native platform)');
      return false;
    }

    try {
      const restored = await this.restoreAuthFromStorage();
      console.log('[AutoLogin] tryAutoLogin after restoreAuthFromStorage', {
        restored,
        isAuthenticated: authService.isAuthenticated(),
        hasSessionUser: !!sessionStorage.getItem('userInfoXpz')
      });

      if (restored && !this.isTokenExpired()) {
        console.log('[AutoLogin] 自动登录成功：从本地存储恢复认证状态');
        try {
          const { onUserLoggedIn } = await import('@/utils/push');
          await onUserLoggedIn();
          console.log('[AutoLogin] 自动登录后已触发保存推送 token');
        } catch (e) {
          console.warn('[AutoLogin] 自动登录后保存推送 token 失败:', e);
        }
        return true;
      }

      if (restored && this.isTokenExpired() && store.getters.refreshToken) {
        try {
          await this.refreshAccessToken();
          console.log('[AutoLogin] 自动登录成功：通过刷新token');
          return true;
        } catch (error) {
          console.log('[AutoLogin] 自动登录失败：刷新token失败', error);
          return false;
        }
      }
      
      const rememberedLoginOk = await this.loginWithRememberedPassword();
      console.log('[AutoLogin] tryAutoLogin after remembered password login', {
        rememberedLoginOk,
        isAuthenticated: authService.isAuthenticated()
      });
      if (rememberedLoginOk) {
        console.log('[AutoLogin] 使用记住的账号密码自动登录成功');
        return true;
      }

      if (!restored && this.enableGuestAutoLogin) {
        console.log('[AutoLogin] 尝试使用游客账号进行自动登录');
        try {
          const guestOk = await this.guestLogin();
          if (guestOk) {
            console.log('[AutoLogin] 游客自动登录成功');
            return true;
          } else {
            console.log('[AutoLogin] 游客自动登录失败');
          }
        } catch (e) {
          console.warn('[AutoLogin] guestLogin error:', e);
        }
      }

      return false;
    } catch (error) {
      console.error('[AutoLogin] 自动登录过程中发生错误:', error);
      return false;
    }
  }

  /**
   * 游客自动登录（仅在启用且原生平台下）
   */
  async loginWithRememberedPassword() {
    if (!this.isAutoLoginEnabled()) {
      console.log('[AutoLogin] loginWithRememberedPassword skipped because auto-login is disabled');
      return false;
    }

    const remembered = this.getRememberedPasswordLogin();
    console.log('[AutoLogin] loginWithRememberedPassword credentials snapshot', {
      hasMobile: !!remembered.mobile,
      hasPassword: !!remembered.password,
      accountType: remembered.accountType,
      blocked: remembered.blocked
    });
    if (remembered.blocked) {
      console.log('[AutoLogin] Auto-login blocked after explicit logout');
      return false;
    }

    if (!remembered.mobile || !remembered.password) {
      console.log('[AutoLogin] No remembered password credentials found');
      return false;
    }

    try {
      const params = {
        mobile: remembered.mobile,
        password: remembered.password,
        type: remembered.accountType || 6,
        source: 'mp'
      };

      console.log('[AutoLogin] Trying remembered password login for mobile:', remembered.mobile);
      const res = await oauth2Api.loginPwdInfo(params);
      if (!(res && res.status && res.ret)) {
        console.warn('[AutoLogin] Remembered password login failed:', res && res.msg);
        return false;
      }

      console.log('[AutoLogin] Remembered password login API succeeded', {
        accountType: res.ret.accountType,
        hasAccessToken: !!res.ret.access_token
      });

      localStorage.setItem('token', res.ret.access_token);
      localStorage.setItem('accountTypeXpz', JSON.stringify(res.ret.accountType || params.type));
      await this.handleLoginSuccess(res.ret);

      try {
        const { onUserLoggedIn } = await import('@/utils/push');
        await onUserLoggedIn();
      } catch (pushErr) {
        console.warn('[AutoLogin] Remembered password login push sync failed:', pushErr);
      }

      return true;
    } catch (error) {
      console.error('[AutoLogin] Remembered password login error:', error);
      return false;
    }
  }

  async guestLogin() {
    if (!this.isAutoLoginEnabled() || !this.enableGuestAutoLogin) {
      return false;
    }

    try {
      const params = {
        mobile: '13999999999',
        password: '636913',
        type: 6,
        source: 'mp'
      };
      console.log('[Auth] guestLogin - calling loginPwdInfo with params:', params);
      const res = await oauth2Api.loginPwdInfo(params);
      console.log('[Auth] guestLogin - response:', res);
      if (res && res.status) {
        await this.handleLoginSuccess(res.ret);
        try {
          const { onUserLoggedIn } = await import('@/utils/push');
          await onUserLoggedIn();
          console.log('[Auth] 游客登录成功，设备码已发送到极光推送服务');
        } catch (pushError) {
          console.warn('[Auth] 发送设备码到极光推送失败，但不影响登录:', pushError);
        }
        return true;
      } else {
        console.warn('[Auth] guestLogin response not ok', res && res.msg);
        return false;
      }
    } catch (error) {
      try {
        console.error('[Auth] guestLogin error:', error, 'response:', error && error.response ? error.response.data : undefined);
      } catch (e) {
        console.error('[Auth] guestLogin error (logging failed):', e);
      }
      return false;
    }
  }

  /**
   * 检查认证状态并在需要时刷新token
   */
  async ensureAuthenticated() {
    if (!this.isAuthenticated()) {
      return false;
    }

    if (this.isTokenExpired()) {
      try {
        await this.refreshAccessToken();
        return true;
      } catch (error) {
        return false;
      }
    }

    return true;
  }
}

// 创建单例实例
const authService = new AuthService();

export default authService;
